/**
 * Child API Keys Table Component (September 2025)
 * 
 * Displays user's child API keys in a mobile-friendly table with copy functionality.
 * Integrates with Convex to fetch and display API keys with environment filtering.
 */

import { useState } from 'react';
import { useQuery } from 'convex/react';
import { useUser } from '@clerk/clerk-react';
import { api } from '../../convex/_generated/api';
import { getConvexHttpUrl } from '../utils/convex';

interface ChildApiKeysTableProps {
  className?: string;
}

interface ApiKeyStats {
  totalRequests: number;
  totalCreditsUsed: number;
  errorCount: number;
  successRate: number;
}

interface ApiKeyData {
  id: string;
  keyHash: string;
  name: string | undefined;
  environment: 'development' | 'staging' | 'production';
  status: string | undefined;
  rateLimitPerMinute: number;
  createdAt: number;
  lastUsedAt?: number;
  stats24h: ApiKeyStats;
}

export function ChildApiKeysTable({ className }: ChildApiKeysTableProps) {
  const { user: clerkUser } = useUser();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<{ keyHash: string; name: string | undefined } | null>(null);
  const [deleteResult, setDeleteResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Fetch API keys from Convex (production only since all child keys are production)
  const apiKeysData = useQuery(api.apiKeys.list, 
    clerkUser ? {
      userId: clerkUser.id,
      environment: 'production', // All child keys are production keys now
      includeRevoked: false
    } : 'skip'
  );

  // Fetch service key for delete operations
  const serviceKey = useQuery(api.users.getUserServiceKey);

  const apiKeys = apiKeysData?.apiKeys ?? [];

  // Copy individual key to clipboard
  const copyKey = async (keyHash: string) => {
    try {
      await navigator.clipboard.writeText(keyHash);
      setCopiedKey(keyHash);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      console.error('Failed to copy key:', error);
      alert('Failed to copy key to clipboard');
    }
  };

  // Copy all keys to clipboard (comma separated)
  const copyAllKeys = async () => {
    const allKeys = apiKeys.map((key: ApiKeyData) => key.keyHash).join(', ');
    try {
      await navigator.clipboard.writeText(allKeys);
      setCopiedKey('ALL');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      console.error('Failed to copy all keys:', error);
      alert('Failed to copy keys to clipboard');
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (keyHash: string, keyName: string) => {
    setKeyToDelete({ keyHash, name: keyName });
    setDeleteModalOpen(true);
  };

  // Close delete modal and reset state
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setKeyToDelete(null);
    setDeleteResult(null);
  };

  // Perform the actual delete operation
  const performDelete = async () => {
    if (!keyToDelete || !serviceKey?.serviceApiKey || serviceKey.serviceApiKey.length === 0) {
      setDeleteResult({ type: 'error', message: 'Service key not available or key not selected.' });
      return;
    }

    setDeletingKey(keyToDelete.keyHash);
    
    try {
      const response = await fetch('${getConvexHttpUrl()}/api/keys/delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': serviceKey.serviceApiKey,
        },
        body: JSON.stringify({
          keyHash: keyToDelete.keyHash,
          reason: 'Deleted via Child API Keys Table'
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setDeleteResult({ 
          type: 'success', 
          message: `API key "${keyToDelete.name ?? 'Unnamed Key'}" has been deleted successfully` 
        });
        // Close modal after success and refresh
        setTimeout(() => {
          closeDeleteModal();
          window.location.reload();
        }, 2000);
      } else {
        setDeleteResult({ 
          type: 'error', 
          message: `Delete failed: ${data.error ?? 'Unknown error'}` 
        });
      }
    } catch (error) {
      console.error('Delete key error:', error);
      setDeleteResult({ 
        type: 'error', 
        message: `Network error: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    } finally {
      setDeletingKey(null);
    }
  };


  // Status badge styling
  const getStatusBadge = (status: string | undefined) => {
    return status === 'active' ? 'text-terminal-green' : 'text-red-400';
  };

  if (!clerkUser) {
    return (
      <div className={`terminal-box ${className}`}>
        <div className="px-4 py-5 text-terminal-orange">
          Please sign in to view API keys.
        </div>
      </div>
    );
  }

  if (apiKeysData === undefined) {
    return (
      <div className={`terminal-box ${className}`}>
        <div className="px-4 py-5 text-terminal-orange">
          Loading API keys...
        </div>
      </div>
    );
  }

  return (
    <div className={`terminal-box ${className}`}>
      <div className="px-4 py-5 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <h3 className="text-lg leading-6 font-medium text-terminal-orange-bright mb-4 sm:mb-0">
            [ CHILD API KEYS ]
          </h3>
          
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Copy All Button */}
            {apiKeys.length > 0 && (
              <button
                onClick={copyAllKeys}
                className="terminal-button text-sm"
              >
                {copiedKey === 'ALL' ? '[ COPIED ALL! ]' : '[ COPY ALL ]'}
              </button>
            )}
          </div>
        </div>

        {/* API Keys Table */}
        {apiKeys.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-terminal-orange-dim text-sm">
              No API keys found. Create a new child API key to get started.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Mobile-friendly card layout for small screens */}
            <div className="sm:hidden space-y-4">
              {apiKeys.map((apiKey: ApiKeyData) => (
                <div key={apiKey.id} className="border border-terminal-orange-dim p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-terminal-orange font-medium truncate">{apiKey.name ?? 'Unnamed Key'}</h4>
                    <div className="flex gap-2">
                      <span className={`text-xs ${getStatusBadge(apiKey.status)}`}>
                        ● {apiKey.status ?? 'unknown'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="font-mono text-xs border border-terminal-orange-dark px-2 py-1 bg-terminal-black text-terminal-green truncate">
                    {apiKey.keyHash}
                  </div>
                  
                  <div className="flex justify-between items-center text-xs text-terminal-orange-dim">
                    <span>Rate: {apiKey.rateLimitPerMinute}/min</span>
                    <span>24h: {apiKey.stats24h.totalRequests} req</span>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyKey(apiKey.keyHash)}
                      className="flex-1 py-2 border border-terminal-orange-dim bg-terminal-black text-terminal-orange text-sm hover:bg-terminal-orange-dark focus:outline-none focus:ring-2 focus:ring-terminal-orange"
                    >
                      {copiedKey === apiKey.keyHash ? '[ COPIED! ]' : '[ COPY KEY ]'}
                    </button>
                    <button
                      onClick={() => openDeleteModal(apiKey.keyHash, apiKey.name ?? 'Unnamed Key')}
                      disabled={deletingKey === apiKey.keyHash || !serviceKey?.serviceApiKey || serviceKey.serviceApiKey.length === 0}
                      className="px-3 py-2 border border-red-500 bg-terminal-black text-red-400 text-sm hover:bg-red-900 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete API key"
                    >
                      {deletingKey === apiKey.keyHash ? '⏳' : '🗑️'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table layout */}
            <table className="hidden sm:table min-w-full">
              <thead>
                <tr className="border-b border-terminal-orange">
                  <th className="text-left py-3 px-4 text-sm font-medium text-terminal-orange">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-terminal-orange">API Key</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-terminal-orange">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-terminal-orange">Rate Limit</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-terminal-orange">24h Usage</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-terminal-orange">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((apiKey: ApiKeyData, index: number) => (
                  <tr 
                    key={apiKey.id} 
                    className={`border-b border-terminal-orange-dark ${index % 2 === 0 ? 'bg-terminal-black' : 'bg-terminal-black/50'}`}
                  >
                    <td className="py-3 px-4 text-sm text-terminal-orange">
                      <div className="font-medium truncate max-w-32" title={apiKey.name ?? 'Unnamed Key'}>
                        {apiKey.name ?? 'Unnamed Key'}
                      </div>
                      <div className="text-xs text-terminal-orange-dim">
                        {new Date(apiKey.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    
                    <td className="py-3 px-4 text-sm">
                      <div className="font-mono text-xs border border-terminal-orange-dark px-2 py-1 bg-terminal-black text-terminal-green max-w-48 truncate" title={apiKey.keyHash}>
                        {apiKey.keyHash}
                      </div>
                    </td>
                    
                    <td className="py-3 px-4 text-sm">
                      <span className={`${getStatusBadge(apiKey.status)} font-medium`}>
                        ● {apiKey.status ?? 'unknown'}
                      </span>
                    </td>
                    
                    <td className="py-3 px-4 text-sm text-terminal-orange">
                      {apiKey.rateLimitPerMinute}/min
                    </td>
                    
                    <td className="py-3 px-4 text-sm text-terminal-orange">
                      <div>{apiKey.stats24h.totalRequests} req</div>
                      <div className="text-xs text-terminal-orange-dim">
                        {apiKey.stats24h.totalCreditsUsed} credits
                      </div>
                    </td>
                    
                    <td className="py-3 px-4 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyKey(apiKey.keyHash)}
                          className="px-3 py-1 border border-terminal-orange-dim bg-terminal-black text-terminal-orange text-xs hover:bg-terminal-orange-dark focus:outline-none focus:ring-2 focus:ring-terminal-orange"
                        >
                          {copiedKey === apiKey.keyHash ? 'COPIED!' : 'COPY'}
                        </button>
                        <button
                          onClick={() => openDeleteModal(apiKey.keyHash, apiKey.name ?? 'Unnamed Key')}
                          disabled={deletingKey === apiKey.keyHash || !serviceKey?.serviceApiKey || serviceKey.serviceApiKey.length === 0}
                          className="px-2 py-1 border border-red-500 bg-terminal-black text-red-400 text-xs hover:bg-red-900 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete API key"
                        >
                          {deletingKey === apiKey.keyHash ? '⏳' : '🗑️'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Stats */}
        {apiKeys.length > 0 && Boolean(apiKeysData) && (
          <div className="mt-6 pt-4 border-t border-terminal-orange-dark">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-terminal-orange-bright">{apiKeysData.summary.total}</div>
                <div className="text-xs text-terminal-orange-dim">Total Keys</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-terminal-green">{apiKeysData.summary.active}</div>
                <div className="text-xs text-terminal-orange-dim">Active</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && keyToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-2 px-2 pb-4 text-center sm:pt-4 sm:px-4 sm:pb-20 sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={closeDeleteModal}
            />

            {/* Modal */}
            <div className="relative inline-block align-middle bg-terminal-black border-2 border-red-500 rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-sm sm:my-8 sm:max-w-md sm:w-full">
              <div className="bg-terminal-black px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-900 bg-opacity-20 sm:mx-0 sm:h-10 sm:w-10">
                    <span className="text-red-400 text-xl">🗑️</span>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-red-400 terminal-glow">
                      [ DELETE API KEY ]
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-terminal-orange">
                        Are you sure you want to permanently delete this API key?
                      </p>
                      <div className="mt-3 p-3 bg-terminal-black border border-terminal-orange-dark rounded">
                        <div className="text-xs text-terminal-orange-dim">Key Name:</div>
                        <div className="text-sm text-terminal-orange font-medium">{keyToDelete.name ?? 'Unnamed Key'}</div>
                        <div className="text-xs text-terminal-orange-dim mt-2">Key Hash:</div>
                        <div className="text-xs font-mono text-terminal-green">{keyToDelete.keyHash.substring(0, 16)}...</div>
                      </div>
                      <p className="text-xs text-red-300 mt-3">
                        ⚠️ This action cannot be undone. The key will be permanently deleted with all metadata.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Result Display */}
              {deleteResult && (
                <div className={`mx-4 mb-4 border ${deleteResult.type === 'success' ? 'border-green-500 bg-green-900' : 'border-red-500 bg-red-900'} bg-opacity-20 p-3 rounded`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className={deleteResult.type === 'success' ? 'text-green-400' : 'text-red-400'}>
                        {deleteResult.type === 'success' ? '✅' : '❌'}
                      </span>
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm ${deleteResult.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                        {deleteResult.message}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-terminal-black border-t border-red-500 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={performDelete}
                  disabled={deletingKey === keyToDelete.keyHash}
                  className="w-full inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-500 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingKey === keyToDelete.keyHash ? '[ DELETING... ]' : '[ DELETE KEY ]'}
                </button>
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="mt-3 w-full inline-flex justify-center px-4 py-2 text-sm font-medium border border-terminal-orange-dim bg-terminal-black text-terminal-orange hover:bg-terminal-orange-dark focus:outline-none focus:ring-2 focus:ring-terminal-orange sm:mt-0 sm:ml-3 sm:w-auto"
                >
                  [ CANCEL ]
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}